"use client";

import { env } from "@/env";

import { buildStorageKey, getLocalStorageItem, setLocalStorageItem } from "./use-local-storage";

/**
 * Cache version for predeposit closed-campaign data.
 * Increment this when the cache format changes to invalidate old caches.
 */
const CACHE_VERSION = 2;

interface CachedClosedData {
  data: unknown;
  version: number;
}

/**
 * Returns the campaign-end ledger version as a BigInt, or undefined if
 * the env var is not set (campaign still active).
 */
export function getCampaignEndLedgerVersion(): bigint | undefined {
  const raw = env.NEXT_PUBLIC_PRE_DEPOSITS_CAMPAIGN_END_LEDGER_VERSION;
  if (!raw) return undefined;
  return BigInt(raw);
}

/**
 * Returns true when the campaign is closed (ledger version env var is set).
 */
export function isCampaignClosed(): boolean {
  return getCampaignEndLedgerVersion() !== undefined;
}

/**
 * Build the view function options object. When the campaign is closed,
 * returns `{ ledgerVersion: BigInt(...) }`. Otherwise returns undefined.
 *
 * Usage: `aptos.view({ payload, options: getViewOptions() })`
 */
export function getViewOptions(): { ledgerVersion: bigint } | undefined {
  const lv = getCampaignEndLedgerVersion();
  if (lv === undefined) return undefined;
  return { ledgerVersion: lv };
}

/**
 * Reads a cached value from localStorage for a closed-campaign query.
 * Returns `undefined` if no valid cache exists (wrong version or missing).
 *
 * @param storageKeyConstant - The constant from local-storage-keys.ts
 * @param identifier - Optional identifier suffix (e.g. user address, referral code)
 */
export function getClosedCache(storageKeyConstant: string, identifier?: string): unknown {
  const baseKey = identifier ? `${storageKeyConstant}-${identifier}` : storageKeyConstant;
  const storageKey = buildStorageKey(baseKey, { network: "predeposit" });
  const raw = getLocalStorageItem(storageKey);
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw) as CachedClosedData;
    if (parsed.version !== CACHE_VERSION) return undefined;
    return parsed.data;
  } catch {
    return undefined;
  }
}

/**
 * Writes a value to localStorage for a closed-campaign query.
 * The data is permanent (no staleness threshold) because on-chain state
 * at a fixed ledger version is immutable.
 *
 * @param storageKeyConstant - The constant from local-storage-keys.ts
 * @param data - The data to cache
 * @param identifier - Optional identifier suffix (e.g. user address, referral code)
 */
export function setClosedCache(
  storageKeyConstant: string,
  data: unknown,
  identifier?: string,
): void {
  const baseKey = identifier ? `${storageKeyConstant}-${identifier}` : storageKeyConstant;
  const storageKey = buildStorageKey(baseKey, { network: "predeposit" });
  const cacheData: CachedClosedData = {
    data,
    version: CACHE_VERSION,
  };
  setLocalStorageItem(storageKey, cacheData);
}
